/* eslint-disable no-magic-numbers */
import TagFilter from "../../apps/data/dashboard/TagFilter";
import { APP_URL, EXTENSIONS_WEB_URL, ZPL_PROTOCOL } from "../constants";

import { AppRoutes } from "../enums";

import BasicRecord from "./BasicRecord";

/**
 * Resolves to webapp URLs from page name and parameters
 */
const URL_MAPPING: BasicRecord = {
    "accountAdmin": "/account/:aid",
    "authFigma": "/auth/figma",
    "authGoogle": "/auth/google",
    "sso-required": "/login/sso-required",
    "downloads": "/downloads",
    "forgotPassword": "/forgot-password",
    "huggingSlack": "/hugging-slack",
    "redeemSlackCode": "/redeem-slack-code",
    "login": "/login",
    "login-sso": "/login/sso",
    "preview": "/project/:pid/preview",
    "profile": "/profile",
    "profile-account": "/profile/account",
    "profile-password": "/profile/password",
    "profile-billing": "/profile/billing",
    "profile-developer": "/profile/developer",
    "profile-connected-apps": "/profile/connected-apps",
    "project": "/project/:bid",
    "project-dashboard": "/project/:pid/dashboard",
    "project-flow": "/project/:pid/flow/:flid",
    "project-flow-empty": "/project/:pid/flow",
    "project-styleguide": "/project/:bid/styleguide",
    "project-styleguide-about": "/project/:bid/styleguide/about",
    "project-styleguide-colors": "/project/:bid/styleguide/colors",
    "project-styleguide-textstyles": "/project/:bid/styleguide/textstyles",
    "project-styleguide-spacing": "/project/:bid/styleguide/spacing",
    "project-styleguide-icons": "/project/:bid/styleguide/icons",
    "project-styleguide-components": "/project/:bid/styleguide/components",
    "project-styleguide-storybook": "/project/:bid/styleguide/storybook",
    "project-screen": "/project/:pid/screen/:sid",
    "project-component": "/project/:bid/styleguide/component/:coid",
    "project-component-version": "/project/:bid/styleguide/component/:coid/version/:vid",
    "project-screen-stage-mode": "/project/:pid/screen/:sid/stage",
    "project-screen-approval-mode": "/project/:pid/screen/:sid/approval/:apid",
    "project-screen-version": "/project/:pid/screen/:sid/version/:vid",
    "projects": "/projects",
    "styleguide": "/styleguide/:bid",
    "styleguide-about": "/styleguide/:bid/about",
    "styleguide-colors": "/styleguide/:bid/colors",
    "styleguide-textstyles": "/styleguide/:bid/textstyles",
    "styleguide-spacing": "/styleguide/:bid/spacing",
    "styleguide-icons": "/styleguide/:bid/icons",
    "styleguide-components": "/styleguide/:bid/components",
    "styleguide-component": "/styleguide/:bid/component/:coid",
    "styleguide-component-version": "/styleguide/:bid/component/:coid/version/:vid",
    "styleguide-storybook": "/styleguide/:bid/storybook",
    "styleguides": "/styleguides",
    "signup": "/signup",
    "signup-sso": "/signup/sso",
    "signupComplete": "/signup/complete",
    "resetPassword": "/reset-password",
    "thanks": "/thanks",
    "verify": "/verify",
    "welcome": "/welcome",
    "workspace-create": "/workspace/create",
    "workspace-members": "/workspace/:oid/members",
    "workspace-profile": "/workspace/:oid/profile",
    "workspace-profile-billing": "/workspace/:oid/profile/billing",
    "workspace-profile-authentication": "/workspace/:oid/profile/authentication",
    "workspace-profile-developer": "/workspace/:oid/profile/developer",
    "workspace-profile-scim": "/workspace/:oid/profile/scim",
    "workspace-projects": "/workspace/:oid/projects",
    "workspace-styleguides": "/workspace/:oid/styleguides",
    "authSamlResult": "/auth/saml/result",
    "activities": "/notifications",
    "workspace-activities": "/notifications/:oid",
    "grant-token": "/grant-token/:redeemerId",
    "multi-factor-authentication-setup": "/mfa-setup",
    "multi-factor-authentication-login": "/login/otp",
    "multi-factor-authentication-recovery-code-login": "/login/recovery-code",
    "personal-create": "/personal/create"
};

// -- Regex patterns for app routes --
// TODO: These are not complete list of regex patterns for all routes. There are only the dynamic routes here. Add all necessary regex patterns.

// - Workspace
const WORKSPACE_ORG_REGEX = /^\/workspace\/([\da-f]{24})\/(projects|styleguides)$/i;

// - Project
const PROJECT_REGEX = /^\/project\/([\da-f]{24})(?:\/(dashboard))?$/i;
const PROJECT_STYLEGUIDE_BASE_REGEX = /^\/project\/([\da-f]{24})\/styleguide$/i;
const PROJECT_STYLEGUIDE_PAGE_REGEX =
    /^\/project\/([\da-f]{24})\/styleguide\/(colors|textstyles|spacing|icons|components|storybook)$/i;

// - Project Flow
const PROJECT_FLOW_EMPTY_REGEX = /^\/project\/([\da-f]{24})\/flow$/i;
const PROJECT_FLOW_REGEX = /^\/project\/([\da-f]{24})\/flow(?:\/([\da-f]{24}))?$/i;

// - Project Component
const PROJECT_COMPONENT_REGEX = /^\/project\/([\da-f]{24})\/styleguide\/component\/([\da-f]{24})$/i;
const PROJECT_COMPONENT_VERSION_REGEX =
    /^\/project\/([\da-f]{24})\/styleguide\/component\/([\da-f]{24})\/version\/([\da-f]{24})$/i;

// - Screen
const SCREEN_REGEX = /^\/project\/([\da-f]{24})\/screen\/([\da-f]{24})$/i;
const SCREEN_VERSION_REGEX = /^\/project\/([\da-f]{24})\/screen\/([\da-f]{24})\/version\/([\da-f]{24})$/i;
const SCREEN_STAGE_REGEX = /^\/project\/([\da-f]{24})\/screen\/([\da-f]{24})\/stage$/i;
const SCREEN_APPROVAL_REGEX = /^\/project\/([\da-f]{24})\/screen\/([\da-f]{24})\/approval\/([\da-f]{24})$/i;

// - Styleguide
const STYLEGUIDE_BASE_REGEX = /^\/styleguide\/([\da-f]{24})$/i;
const STYLEGUIDE_PAGE_REGEX =
    /^\/styleguide\/([\da-f]{24})\/(about|colors|textstyles|spacing|icons|components|storybook)$/i;

// - Styleguide Component
const STYLEGUIDE_COMPONENT_REGEX = /^\/styleguide\/([\da-f]{24})\/component\/([\da-f]{24})$/i;
const STYLEGUIDE_COMPONENT_VERSION_REGEX =
    /^\/styleguide\/([\da-f]{24})\/component\/([\da-f]{24})\/version\/([\da-f]{24})$/i;

// - Notifications
const NOTIFICATIONS_REGEX = /^\/notifications\/([\da-f]{24})$/i;

// -- Regex patterns for other routes --

// - Workspace profile
const WORKSPACE_PROFILE_BASE_REGEX = /^\/workspace\/([\da-f]{24})\/(members|profile)$/i;
const WORKSPACE_PROFILE_TABS_REGEX = /^\/workspace\/([\da-f]{24})\/profile\/(billing|authentication|developer|scim)$/i;

// - Account
const ACCOUNT_REGEX = /^\/account\/([\da-f]{24})$/i;

// - Grant token
const GRANT_TOKEN_REGEX = /^\/grant-token\/([\da-f]{24})$/i;

type DynamicPathToStaticPathParams = {
    pattern: RegExp;
    staticPath?: string;
    replace?: {
        search: RegExp;
        value: string;
    };
};

const dynamicPathToStaticPathMap: DynamicPathToStaticPathParams[] = [
    // -- App routes --
    // - Workspace
    { pattern: WORKSPACE_ORG_REGEX, replace: { search: /[\da-f]{24}/, value: ":oid" } },
    // - Project
    { pattern: PROJECT_REGEX, staticPath: "/project/:pid" },
    { pattern: PROJECT_STYLEGUIDE_BASE_REGEX, staticPath: "/project/:pid/styleguide" },
    { pattern: PROJECT_STYLEGUIDE_PAGE_REGEX, replace: { search: /[\da-f]{24}/, value: ":pid" } },
    // - Project Flow
    { pattern: PROJECT_FLOW_EMPTY_REGEX, staticPath: "/project/:pid/flow" },
    { pattern: PROJECT_FLOW_REGEX, staticPath: "/project/:pid/flow/:flid" },
    // - Project Component
    { pattern: PROJECT_COMPONENT_VERSION_REGEX, staticPath: "/project/:pid/styleguide/component/:coid/version/:vid" },
    { pattern: PROJECT_COMPONENT_REGEX, staticPath: "/project/:pid/styleguide/component/:coid" },
    // - Screen
    { pattern: SCREEN_REGEX, staticPath: "/project/:pid/screen/:sid" },
    { pattern: SCREEN_STAGE_REGEX, staticPath: "/project/:pid/screen/:sid/stage" },
    { pattern: SCREEN_APPROVAL_REGEX, staticPath: "/project/:pid/screen/:sid/approval/:apid" },
    { pattern: SCREEN_VERSION_REGEX, staticPath: "/project/:pid/screen/:sid/version/:vid" },
    // - Styleguide
    { pattern: STYLEGUIDE_BASE_REGEX, staticPath: "/styleguide/:stid" },
    { pattern: STYLEGUIDE_PAGE_REGEX, replace: { search: /[\da-f]{24}/, value: ":stid" } },
    // - Styleguide Component
    { pattern: STYLEGUIDE_COMPONENT_VERSION_REGEX, staticPath: "/styleguide/:stid/component/:coid/version/:vid" },
    { pattern: STYLEGUIDE_COMPONENT_REGEX, staticPath: "/styleguide/:stid/component/:coid" },
    // - Notifications
    { pattern: NOTIFICATIONS_REGEX, staticPath: "/notifications/:oid" },
    // -- Other routes --
    // - Workspace profile
    { pattern: WORKSPACE_PROFILE_BASE_REGEX, replace: { search: /[\da-f]{24}/, value: ":oid" } },
    { pattern: WORKSPACE_PROFILE_TABS_REGEX, replace: { search: /[\da-f]{24}/, value: ":oid" } },
    // - Account
    { pattern: ACCOUNT_REGEX, staticPath: "/account/:aid" },
    // - Grant token
    { pattern: GRANT_TOKEN_REGEX, staticPath: "/grant-token/:redeemerId" }
];

function joinUrlParams(params: BasicRecord<string | string[]>): string {
    if (!Object.keys(params)) {
        return "";
    }

    const queryParams: [string, string][] = [];

    Object.keys(params).forEach(k => {
        if (!params[k]) {
            return;
        }

        if (Array.isArray(params[k])) {
            (params[k] as string[]).forEach(v => {
                queryParams.push([k, v]);
            });
        } else {
            queryParams.push([k, params[k] as string]);
        }
    });

    return queryParams.map(paramPair => paramPair.map(encodeURIComponent).join("=")).join("&");
}

function replaceUrlTokens(urlPattern: string, params: BasicRecord): string {
    return Object.entries(params).reduce((url, [token, value]) => (
        url.replace(new RegExp(`:${token}`), value)
    ), urlPattern);
}

function urlResolve(name: string, { origin = location.origin, url = {}, query = {}, hash = {} } = {}): string {
    const resolvedURL = new URL(replaceUrlTokens(URL_MAPPING[name], url), origin);

    if (query && !isEmpty(query)) {
        resolvedURL.search = joinUrlParams(query);
    }

    if (hash && !isEmpty(hash)) {
        resolvedURL.hash = joinUrlParams(hash);
    }

    return resolvedURL.toString();
}

function parseUrlParams(paramString: string): BasicRecord<string[]> {
    const paramStringWithoutQuestionMark = paramString[0] === "?" ? paramString.substring(1) : paramString;
    const pairs = paramStringWithoutQuestionMark
        .split("&")
        .map(pair =>
            pair.split("=")
                .map(single => single.replace(/\+/g, "%20"))
                .map(decodeURIComponent)
        );

    const keys = Array.from(new Set(pairs.map(pair => pair[0])));

    const params = keys.reduce((obj, key) => {
        obj[key] = [];
        return obj;
    }, {} as BasicRecord<string[]>);

    pairs.forEach(pair => {
        const paramValues = params[pair[0]];

        if (!paramValues) {
            return;
        }

        if (pair[1]) {
            paramValues.push(pair[1]);
        }
    });

    return params;
}

function isEmpty<T>(obj: BasicRecord<T>): boolean {
    return Object.keys(obj).length === 0;
}

function getAppRouteFromPathname(pathname: string): AppRoutes | null {
    const workspaceRegex = /^(?:\/workspace\/([\da-f]{24}))?\/(projects|styleguides)?$/i;
    const projectRegex = /\/project\/([\da-f]{24})(?:\/(dashboard))?$/i;
    const styleguideRegex = /\/styleguide\/([\da-f]{24})/i;
    const projectStyleguideRegex = /\/project\/([\da-f]{24})\/styleguide/i;
    const screenRegex = /\/project\/([\da-f]{24})\/screen\/([\da-f]{24})?$/i;
    const screenVersionRegex = /\/project\/([\da-f]{24})\/screen\/([\da-f]{24})\/version\/([\da-f]{24})?$/i;
    const projectComponentRegex = /\/project\/([\da-f]{24})\/styleguide\/component\/([\da-f]{24})?$/i;
    const styleguideComponentRegex = /\/styleguide\/([\da-f]{24})\/component\/([\da-f]{24})?$/i;
    const projectComponentVersionRegex =
        /\/project\/([\da-f]{24})\/styleguide\/component\/([\da-f]{24})\/version\/([\da-f]{24})?$/i;
    const styleguideComponentVersionRegex =
        /\/styleguide\/([\da-f]{24})\/component\/([\da-f]{24})\/version\/([\da-f]{24})?$/i;
    const notificationsRegex = /^\/notifications(?:\/)?([\da-f]{24})?$/i;
    const flowRegex = /\/project\/([\da-f]{24})\/flow(?:\/)?([\da-f]{24})?$/i;
    let appRoute = null;

    if (workspaceRegex.test(pathname)) {
        appRoute = AppRoutes.Workspace;
    } else if (projectRegex.test(pathname)) {
        appRoute = AppRoutes.Project;
    } else if (projectComponentRegex.test(pathname)) {
        appRoute = AppRoutes.ProjectComponent;
    } else if (styleguideComponentRegex.test(pathname)) {
        appRoute = AppRoutes.StyleguideComponent;
    } else if (projectComponentVersionRegex.test(pathname)) {
        appRoute = AppRoutes.ProjectComponentVersion;
    } else if (styleguideComponentVersionRegex.test(pathname)) {
        appRoute = AppRoutes.StyleguideComponentVersion;
    } else if (styleguideRegex.test(pathname)) {
        appRoute = AppRoutes.Styleguide;
    } else if (projectStyleguideRegex.test(pathname)) {
        appRoute = AppRoutes.ProjectStyleguide;
    } else if (screenRegex.test(pathname)) {
        appRoute = AppRoutes.Screen;
    } else if (screenVersionRegex.test(pathname)) {
        appRoute = AppRoutes.ScreenVersion;
    } else if (notificationsRegex.test(pathname)) {
        appRoute = AppRoutes.Notifications;
    } else if (flowRegex.test(pathname)) {
        appRoute = AppRoutes.Flow;
    }

    return appRoute;
}

/**
 * Converts given dynamic pathname to static pathname
 */
// eslint-disable-next-line complexity
function getStaticPathname(pathname: string): string {
    for (const { pattern, staticPath, replace } of dynamicPathToStaticPathMap) {
        if (pattern.test(pathname)) {
            return staticPath || pathname.replace(replace!.search, replace!.value);
        }
    }

    return pathname;
}

function parseProjectsURI(uri: URL) {
    const pid = uri.searchParams.getAll("pid");
    const widOrOid = uri.searchParams.get("wid") || uri.searchParams.get("oid");
    const seid = uri.searchParams.get("seid");

    return {
        urlSlug: widOrOid ? "workspace-projects" : "projects",
        query: seid ? {
            seid
        } : {
            pid
        },
        url: widOrOid ? {
            oid: widOrOid
        } : {}
    };
}

function parseStyleguidesURI(uri: URL) {
    const stid = uri.searchParams.getAll("stid");
    const widOrOid = uri.searchParams.get("wid") || uri.searchParams.get("oid");

    return {
        urlSlug: widOrOid ? "workspace-styleguides" : "styleguides",
        url: widOrOid ? { oid: widOrOid } : {},
        query: { stid }
    };
}

function parseScreenURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const sid = uri.searchParams.get("sid");
    const vid = uri.searchParams.get("vid");

    return {
        urlSlug: vid ? "project-screen-version" : "project-screen",
        url: { pid, sid, vid }
    };
}

function parseDotURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const sid = uri.searchParams.get("sid");
    const did = uri.searchParams.getAll("did");
    const cmid = uri.searchParams.getAll("cmid");

    const flid = uri.searchParams.get("flid");
    const cmids = uri.searchParams.getAll("cmids");
    if (flid) {
        return {
            urlSlug: "project-flow",
            url: { pid, flid },
            query: { did, cmid: cmids }
        };
    }

    return {
        urlSlug: "project-screen",
        url: { pid, sid },
        query: { did, cmid }
    };
}

function parseStyleguideURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");
    const cid = uri.searchParams.getAll("cid");
    const tsid = uri.searchParams.getAll("tsid");
    const sptid = uri.searchParams.getAll("sptid");
    const coid = uri.searchParams.getAll("coid");

    return {
        urlSlug: stid ? "styleguide" : "project-styleguide",
        url: { bid: pid ?? stid },
        query: { cid, tsid, sptid, coid }
    };
}

function parseColorsURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");
    const cid = uri.searchParams.getAll("cid");

    return {
        urlSlug: stid ? "styleguide-colors" : "project-styleguide-colors",
        url: { bid: pid ?? stid },
        query: { cid }
    };
}

function parseTextStylesURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");
    const tsid = uri.searchParams.getAll("tsid");

    return {
        urlSlug: stid ? "styleguide-textstyles" : "project-styleguide-textstyles",
        url: { bid: pid ?? stid },
        query: { tsid }
    };
}

function parseSpacingURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");
    const sptid = uri.searchParams.getAll("sptid");

    return {
        urlSlug: stid ? "styleguide-spacing" : "project-styleguide-spacing",
        url: { bid: pid ?? stid },
        query: { sptid }
    };
}

function parseIconsURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");

    return {
        urlSlug: stid ? "styleguide-icons" : "project-styleguide-icons",
        url: { bid: pid ?? stid }
    };
}

function parseComponentsURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");
    const coid = uri.searchParams.getAll("coid");
    const cseids = uri.searchParams.getAll("cseid");
    const seids = uri.searchParams.getAll("seid");
    const seid = [...new Set([...seids, ...cseids])];

    return {
        urlSlug: stid ? "styleguide-components" : "project-styleguide-components",
        url: { bid: pid ?? stid },
        query: { coid, seid }
    };
}

function parseFlowURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const flid = uri.searchParams.get("flid");
    const noid = uri.searchParams.getAll("noid");

    return {
        urlSlug: "project-flow",
        url: { pid, flid },
        query: { noid }
    };
}

function parseFlowGroupURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const flid = uri.searchParams.get("flid");
    const fgid = uri.searchParams.getAll("fgid");

    return {
        urlSlug: "project-flow",
        url: { pid, flid },
        query: { fgid }
    };
}

function parseProjectDashboardURI(uri: URL) {
    const pid = uri.searchParams.get("pid");
    const sid = uri.searchParams.getAll("sid");
    const seid = uri.searchParams.getAll("seid");

    return {
        urlSlug: "project-dashboard",
        url: { pid },
        query: { sid, seid }
    };
}

/**
 * Parse given app uri. uri is an URL instance
 */
// eslint-disable-next-line complexity
function parseAppURI(uri: URL): {
    urlSlug: string;
    url: {
        oid?: string;
        bid?: string | null;
        pid?: string | null;
        sid?: string | null;
        vid?: string | null;
    };
    query?: {
        pid?: string[];
        stid?: string[];
        did?: string[];
        cmid?: string[];
        cid?: string[];
        tsid?: string[];
        sptid?: string[];
        coid?: string[];
        seid?: string | string[];
        noid?: string[];
        fgid?: string[];
    };
} | null {
    const pathnameOrHostname = uri.pathname || `//${uri.hostname}`;

    if (pathnameOrHostname.startsWith("//projects")) {
        return parseProjectsURI(uri);
    } else if (pathnameOrHostname.startsWith("//styleguides")) {
        return parseStyleguidesURI(uri);
    }

    const pid = uri.searchParams.get("pid");
    const stid = uri.searchParams.get("stid");

    if (!pid && !stid) {
        return null;
    }

    if (pathnameOrHostname.startsWith("//screen")) {
        return parseScreenURI(uri);
    } else if (pathnameOrHostname.startsWith("//dot")) {
        return parseDotURI(uri);
    } else if (pathnameOrHostname.startsWith("//styleguide")) {
        return parseStyleguideURI(uri);
    } else if (pathnameOrHostname.startsWith("//colors")) {
        return parseColorsURI(uri);
    } else if (pathnameOrHostname.startsWith("//textStyles")) {
        return parseTextStylesURI(uri);
    } else if (pathnameOrHostname.startsWith("//spacing")) {
        return parseSpacingURI(uri);
    } else if (pathnameOrHostname.startsWith("//icons")) {
        return parseIconsURI(uri);
    } else if (pathnameOrHostname.startsWith("//components")) {
        return parseComponentsURI(uri);
    } else if (pathnameOrHostname.startsWith("//flowGroup")) {
        return parseFlowGroupURI(uri);
    } else if (pathnameOrHostname.startsWith("//flow")) {
        return parseFlowURI(uri);
    }

    return parseProjectDashboardURI(uri);
}

/**
 * Convert given uri to web url
 */
function convertURItoWebURL(uri: URL) {
    if (uri.protocol !== ZPL_PROTOCOL) {
        return;
    }

    const isNavigation = uri.hostname === "navigation" || uri.pathname.replace(/^\/+/, "") === "navigation";
    if (isNavigation && uri.searchParams.has("target")) {
        const target = uri.searchParams.get("target");
        const decodedTarget = decodeURIComponent(target!);

        return `${APP_URL}${decodedTarget}`;
    }

    const {
        urlSlug,
        url = {},
        query = {}
    } = parseAppURI(uri) ?? {};

    if (!urlSlug) {
        return;
    }

    return urlResolve(urlSlug, { url, query });
}

const projectPatternStr = "/project/([0-9a-z]{24})";
const screenPatternStr = "/screen/([0-9a-z]{24})";
const versionPatternStr = "/version/([0-9a-z]{24})";
const screenAndVersionPatternStr = `${screenPatternStr}(?:${versionPatternStr})?`; // There could be a screen link without version
const projectScreenAndVersionPatternStr = `${projectPatternStr}(?:${screenAndVersionPatternStr})?`; // There could be a project link without screen or version
const projectScreenAndVersionPattern = new RegExp(projectScreenAndVersionPatternStr, "i");

function parseUrlPath(path: string) {
    const match = path.match(projectScreenAndVersionPattern);

    if (!match) {
        return null;
    }

    const params: {
        pid: string;
        sid?: string;
        vid?: string;
    } = {
        pid: decodeURIComponent(match[1])
    };

    if (match[2]) {
        params.sid = decodeURIComponent(match[2]);
    }

    if (match[3]) {
        params.vid = decodeURIComponent(match[3]);
    }

    return params;
}

function getTagQueryParams(tagFilter: TagFilter) {
    const { tids, untagged } = tagFilter;
    return untagged ? { untagged: "true" } : { tid: tids };
}

function getExtensionWebsiteUrl({ eid, organizationId }: {
    eid?: string;
    organizationId?: string;
} = {}): string {
    if (eid) {
        return `${EXTENSIONS_WEB_URL}/${eid}`;
    }

    if (organizationId && organizationId !== "user") {
        return `${EXTENSIONS_WEB_URL}?oid=${organizationId}`;
    }

    return EXTENSIONS_WEB_URL;
}

const isValidUrl = (string?: string): boolean => {
    try {
        const url = new URL(string!); // empty string inputs return false via catch blcok
        return !!url;
    } catch (_) {
        return false;
    }
};

function getFormattedUrl(fullUrl: string, options?: {
    trimTrailingSlash?: boolean;
}) {
    if (!fullUrl) {
        return "";
    }

    const { host, pathname } = new URL(fullUrl);

    let formattedUrl = `${host}${pathname}`;

    if (options?.trimTrailingSlash && formattedUrl.endsWith("/")) {
        formattedUrl = formattedUrl.slice(0, -1);
    }

    return formattedUrl;
}

export {
    joinUrlParams,
    parseUrlPath,
    parseUrlParams,
    getTagQueryParams,
    urlResolve,
    getExtensionWebsiteUrl,
    isValidUrl,
    parseAppURI,
    convertURItoWebURL,
    getAppRouteFromPathname,
    getFormattedUrl,
    getStaticPathname
};
